import React, { useState } from 'react';
import './App.css';
import "./mobile_styles/App.css"
import "./styles/theme-fonts.css"
import "./styles/cart-layout.css"
import Topbar from './components/Topbar';
import DefaultSidebar from './components/DefaultSidebar';
import TopbarNav from './components/TopbarNav';
import AppContext from './AppContext';
import SignIn from './components/SignIn';
import { HiOutlineShoppingCart, HiShoppingCart } from 'react-icons/hi';
import { useTheme } from './hooks/ThemeContext';
import { Link, useLocation } from 'react-router-dom';
import { useConfig } from './hooks/ConfigProvider';
import { getRemoteEnv, isSingleStore } from './config/remoteEnv';
import { useRestaurant } from './hooks/RestaurantContext';

type Props = {
  children: React.ReactNode,
  cartCount?: number,
  sidebarPages?: any,
  showSidebar?: boolean,
  showTopbar?: boolean,
  logo?: string,
  hideLogo?: boolean,
}

function RootPage(props: Props) {

  const [activeUserId, setActiveUserId] = useState("unset");
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  const [showTopbar, setShowTopbar] = React.useState(true);
  const [showDefaultSidebar, setShowDefaultSidebar] = React.useState(true);

  const location = useLocation();
  const [rValue, setRValue] = useState<String | null>(null);


  React.useEffect(() => {
    if (props.showSidebar) {
      setShowDefaultSidebar(props.showSidebar);
    }
    else {
      setShowDefaultSidebar(false);
      setShowSidebarToggle(false);
    }
    if (props.showTopbar) {
      setShowTopbar(props.showTopbar);
    }

  }, [props.showSidebar, props.showTopbar])

  React.useEffect(() => {
  const searchParams = new URLSearchParams(location.search);
  const r = searchParams.get('r');
  console.log('URL search:', location.search);
  console.log('Extracted r value:', r);
  setRValue(r);
  
}, [location.search]);

  const [overlappingSidebar, setOverlappingSidebar] = React.useState(false);

  const [mobileBottomBar, setMobileBottomBar] = React.useState(true);

  const [nightMode, setNightMode] = React.useState(false);

  const [showCartButton, setShowCartButton] = React.useState(false);
  const [showSidebarToggle, setShowSidebarToggle] = React.useState(true);

  const { theme } = useTheme();
  const { config } = useConfig();

  // The areas iOS reveals during overscroll/pull-to-refresh show the BODY
  // background — keep it on-brand instead of the hardcoded CSS fallback.
  React.useEffect(() => {
    if (theme?.backgroundColor) {
      document.body.style.backgroundColor = theme.backgroundColor;
      document.documentElement.style.backgroundColor = theme.backgroundColor;
    }
  }, [theme?.backgroundColor]);

  const { restaurantId } = useRestaurant();
  const restaurant = config?.restaurants?.[restaurantId];

  // "Menu only" = ordering is off for this store (same rule used by OrderPage /
  // ProductCard). In that case the primary nav item reads "Menu" instead of
  // "Order". Done here so the desktop top nav and mobile bottom bar match.
  const isMenuOnly =
    (getRemoteEnv().menuOnly || !restaurant?.general?.onlineOrderingEnabled) &&
    !window.location.hostname.includes("demo.");

  const navPages = React.useMemo(
    () =>
      (props.sidebarPages || []).map((page: any) =>
        isMenuOnly && page.buttonText === "Order"
          ? { ...page, buttonText: "Menu" }
          : page
      ),
    [props.sidebarPages, isMenuOnly]
  );

  // Account now lives in the profile dropdown on desktop, so keep it out of the
  // top nav (the mobile bottom bar still shows it).
  const topNavPages = React.useMemo(
    () => navPages.filter((page: any) => page.buttonText !== "Account"),
    [navPages]
  );

  const accountUrl = React.useMemo(() => {
    const account = (props.sidebarPages || []).find(
      (page: any) => page.buttonText === "Account"
    );
    if (!account) return "/account";
    return Array.isArray(account.url) ? account.url[0] : account.url;
  }, [props.sidebarPages]);

  const [appContext, setAppContext] = React.useState({
    "appTheme": "dark"
  })

  //const location = useLocation();

  // Parse query parameters
  const searchParams = new URLSearchParams(location.search);
  //const rValue = searchParams.get('r'); // Extract ?r=abc

  // Check if current URL is `/order`
  const isOrderPage = location.pathname === '/order' || location.pathname === "/test/order";

  // "Document flow" mode (mobile): browsing pages scroll the DOCUMENT, so
  // Safari collapses its toolbar on scroll (CNBC-style, more room for the
  // app) with sticky top/bottom bars. Cart/checkout, admin and live-orders
  // keep the constrained inner-scroller layout — they rely on pinned
  // footers and their own scroll panes.
  const docFlow = !(
    location.pathname.startsWith('/cart') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/live')
  );
  // Doc-flow is a MOBILE layout (matches the max-width:999px CSS block);
  // desktop (>=1000px, the only other mode) keeps its constrained panes.
  const docFlowMobile = docFlow && typeof window !== "undefined" && window.innerWidth < 1000;

  // iOS keyboard leftovers (cart/checkout): focusing an input makes Safari
  // scroll the WINDOW to reveal it even though constrained routes scroll in
  // inner panes — and dismissing the keyboard often leaves that offset
  // behind: the app sits shoved up with dead themed space under the bottom
  // bar and no document scrollbar to recover with. On constrained routes a
  // non-zero window scroll is NEVER legitimate, so snap back whenever the
  // keyboard goes away. (Doc-flow pages scroll the document by design —
  // they're excluded.)
  React.useEffect(() => {
    if (docFlowMobile) return;
    const snapBack = () => {
      const el = document.activeElement as HTMLElement | null;
      const typing = !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (!typing && (window.scrollY > 0 || document.documentElement.scrollTop > 0)) {
        window.scrollTo(0, 0);
      }
    };
    const onFocusOut = () => { window.setTimeout(snapBack, 300); };
    document.addEventListener("focusout", onFocusOut);
    const vv = window.visualViewport;
    let lastH = vv ? vv.height : 0;
    const onVvResize = () => {
      if (!vv) return;
      const grew = vv.height > lastH + 50; // keyboard dismissed
      lastH = vv.height;
      if (grew) window.setTimeout(snapBack, 50);
    };
    vv?.addEventListener("resize", onVvResize);
    return () => {
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", onVvResize);
    };
  }, [docFlowMobile]);

    return (
      <AppContext.Provider value={appContext}>
      <div className={`App ${docFlow ? "doc-flow" : ""}`} style={{display: "flex", height: "80px", backgroundColor: theme.backgroundColor}}>

        {showTopbar && <Topbar 
          setActiveUserId={setActiveUserId}
          setSidebarOpen={setSidebarOpen}
          showSidebarToggle={showSidebarToggle} 
          height={"var(--dp-topbar-h)"}
          mobileBottomBar={mobileBottomBar}
          borderBottom='5px solid #0a1239'
          logo={props.logo || undefined}
          hideLogo={false}
          // hideLogo={props.hideLogo || true}
        >

        {navPages.length > 0 && <TopbarNav sidebarPages={topNavPages} />}

        {(isOrderPage || rValue || isSingleStore()) && (props.cartCount == 0 || props.cartCount) && ((!getRemoteEnv().menuOnly && restaurant?.general.onlineOrderingEnabled) || window.location.hostname.includes("demo.")) && (

          <Link to={`/cart?r=${rValue}`} className="auth-button topbar-cart-link"  style={{ textDecoration: 'none', zIndex: "1000", fontSize: "x-large", height: "100%", color: theme.topbarIconColor, marginRight: "10px"}}>
              <span className="user-icon" style={{}}><HiShoppingCart /><span style={{fontSize: "small"}}>&nbsp;({props.cartCount || 0})</span></span>
          </Link>
        )}

          <SignIn setActiveUserId={setActiveUserId} accountUrl={accountUrl} />
          
        </Topbar>}

        {/* overflowY hidden would make this the sticky bars' scrollport (they
            would never pin to the viewport in doc-flow), so it only applies
            in the constrained layout. */}
        <div className={mobileBottomBar ? 'below-topbar horizontal-bar' : 'below-topbar vertical-bar'} style={{display: "flex", width: "100%", flexGrow: 1, ...(docFlowMobile ? {} : {overflowY: "hidden"})}}>
          
            {showDefaultSidebar && sidebarOpen && props.sidebarPages &&
            
              <DefaultSidebar 
                sidebarPages={navPages}
                overlappingBar={overlappingSidebar} 
                showSidebar={showDefaultSidebar} 
                setActiveUserId={setActiveUserId}
                mobileBottomBar={mobileBottomBar}
                sleekBar={true}
                //icon size
              />
              
            }

            <div className='page-container' style={{}}>

              {props.children}

            </div>
        </div>


      </div>
      </AppContext.Provider>
    );
}

export default RootPage;